import { BuildComponentTypeDefinition, BaseType } from "../types/Enums";

/**
 * Maps BaseType enum values to their corresponding icon paths.
 * Used for fallback when specific component type icons don't exist.
 */
export const BASE_TYPE_ICON_MAP: Partial<Record<BaseType, string>> = {
    [BaseType.Roof]: "/Images/Basetypes/roof.png",
    [BaseType.RoofSafety]: "/Images/Basetypes/roof_safety.png",
    [BaseType.Facade]: "/Images/Basetypes/facade.png",
    [BaseType.Balcony]: "/Images/Basetypes/balcony.png",
    [BaseType.Foundation]: "/Images/Basetypes/foundation.png",
    [BaseType.Chimney]: "/Images/Basetypes/chimney.png",
    [BaseType.RoofDrainage]: "/Images/Basetypes/roof_drainage.png",
    [BaseType.GroundDrainage]: "/Images/Basetypes/ground_drainage.png",
    [BaseType.Riser]: "/Images/Basetypes/riser.png",
    [BaseType.Miscellaneous]: "/Images/Basetypes/miscellaneous.png",
    [BaseType.Utility]: "/Images/Basetypes/utility.png",
    [BaseType.FixedFurnishing]: "/Images/Basetypes/fixed_furnishing.png",
    [BaseType.Outdoor]: "/Images/Basetypes/outdoor.png",
    [BaseType.Wall]: "/Images/Basetypes/wall.png",
    [BaseType.Ceiling]: "/Images/Basetypes/ceiling.png",
    [BaseType.Floor]: "/Images/Basetypes/floor.png",
    [BaseType.Window]: "/Images/types/window.png",
    [BaseType.Door]: "/Images/types/door.png",
    [BaseType.SheetMetal]: "/Images/Basetypes/sheet_metal.png",
    [BaseType.Membrane]: "/Images/Basetypes/membrane.png",
    [BaseType.Structural]: "/Images/Basetypes/structural.png",
    [BaseType.GeoTechnics]: "/Images/Basetypes/geo_technics.png",
    [BaseType.LockMechanism]: "/Images/Basetypes/lock_mechanism.png",
};

/**
 * Gets the icon path for a BaseType directly.
 *
 * @param baseType The BaseType enum value
 * @returns Full path to the icon image, or placeholder path if not found
 */
export const getBaseTypeIconPath = (baseType: BaseType | undefined): string => {
    if (!baseType) {
        return "/Images/placeholder.png";
    }
    return BASE_TYPE_ICON_MAP[baseType] || "/Images/placeholder.png";
};

/**
 * Gets the icon paths for a component type with fallback logic.
 * Returns primary path and fallback paths for cascading image loading.
 *
 * @param specificTypeValue The specific component type key (e.g., "facade_vertical_siding")
 * @param baseType The BaseType to fall back to if specificType icon doesn't exist
 * @param getBaseTypeFromSpecificType Optional function to resolve baseType from specificType
 * @returns Object with primary icon path and array of fallback paths
 */
export const getComponentIconPaths = (
    specificTypeValue: string | undefined,
    baseType: BaseType | undefined,
    getBaseTypeFromSpecificType?: (specificType: string) => BaseType | undefined
): { primary: string; fallbacks: string[] } => {
    const fallbacks: string[] = [];

    // Determine the baseType icon path
    let baseTypeIconPath: string | undefined;
    if (specificTypeValue && getBaseTypeFromSpecificType) {
        const resolvedBaseType = getBaseTypeFromSpecificType(specificTypeValue);
        if (resolvedBaseType) {
            baseTypeIconPath = BASE_TYPE_ICON_MAP[resolvedBaseType];
        }
    }
    if (!baseTypeIconPath && baseType) {
        baseTypeIconPath = BASE_TYPE_ICON_MAP[baseType];
    }

    // Primary path: try specific type icon first, then baseType icon
    let primary: string;
    if (specificTypeValue) {
        primary = `/Images/types/${specificTypeValue}.png`;
        // Add baseType as fallback
        if (baseTypeIconPath) {
            fallbacks.push(baseTypeIconPath);
        }
    } else if (baseTypeIconPath) {
        // No specific type, use baseType icon as primary
        primary = baseTypeIconPath;
    } else {
        primary = '/Images/placeholder.png';
    }

    // Final fallback: placeholder (if not already the primary)
    if (primary !== '/Images/placeholder.png') {
        fallbacks.push('/Images/placeholder.png');
    }

    return { primary, fallbacks };
};

/**
 * Gets the icon path for a component type with fallback logic.
 * For backwards compatibility - use getComponentIconPaths for new code.
 *
 * @param componentType The component type key (e.g., "facade_vertical_siding")
 * @param buildComponentTypes The map of all component type definitions
 * @returns Path to the icon image (without /Images/ prefix for legacy compatibility)
 */
export const getComponentTypeIconPath = (
    componentType: string | undefined,
    buildComponentTypes: Record<string, BuildComponentTypeDefinition>,
): string => {
    if (!componentType) {
        return "placeholder.png";
    }

    // First, try the specific component type icon
    return `types/${componentType}.png`;
};

/**
 * Gets the fallback icon path for a component type based on its BaseType.
 *
 * @param componentType The component type key
 * @param buildComponentTypes The map of all component type definitions
 * @returns Path to the BaseType icon (without /Images/ prefix), or placeholder if not found
 */
export const getComponentTypeFallbackIconPath = (
    componentType: string | undefined,
    buildComponentTypes: Record<string, BuildComponentTypeDefinition>
): string => {
    if (!componentType) {
        return "placeholder.png";
    }

    const typeDef = buildComponentTypes[componentType];
    if (!typeDef) {
        return "placeholder.png";
    }

    const path = BASE_TYPE_ICON_MAP[typeDef.baseType];
    // Remove /Images/ prefix for backwards compatibility
    return path ? path.replace('/Images/', '') : "placeholder.png";
};
